import React, { useState, useCallback, useContext, useEffect } from 'react';
import {
  Typography, Button, Select, Input, Space, message, Divider,
  List, Avatar, Popconfirm, Card, Tag, Spin,
} from 'antd';
import {
  BulbOutlined, SaveOutlined, UserOutlined,
  CommentOutlined, EditOutlined, DeleteOutlined,
} from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../api';
import { UserContext } from '../App';
import { formatTS } from '../utils/time';
import CommentBox, { CommentAttachments } from '../components/CommentBox';
import LinkifiedText from '../components/LinkifiedText';
import { useBackFallback, usePageLabel } from '../components/BackChip';
import UserAvatar from '../components/UserAvatar';

const STATUS_OPTIONS = [
  { label: 'Submitted',      value: 'submitted' },
  { label: 'In Review',      value: 'in_review' },
  { label: 'In Development', value: 'in_development' },
  { label: 'Completed',      value: 'completed' },
  { label: 'Canceled',       value: 'canceled' },
];

const STATUS_COLOR = {
  submitted:      'blue',
  in_review:      'orange',
  in_development: 'processing',
  completed:      'green',
  canceled:       'red',
};

const STATUS_LABEL = {
  submitted:      'Submitted',
  in_review:      'In Review',
  in_development: 'In Development',
  completed:      'Completed',
  canceled:       'Canceled',
};

export default function IdeaDetailPage() {
  const { id }      = useParams();
  const currentUser = useContext(UserContext);
  const isAdmin     = currentUser?.Role === 'admin';

  const [idea,          setIdea]          = useState(null);
  // Names this page in Recent and on the next page's Back chip.
  const ideaText = idea?.ShortDescription || '';
  const ideaShort = ideaText.length > 60 ? `${ideaText.slice(0, 57)}...` : ideaText;
  usePageLabel(idea ? `Idea #${id}${ideaShort ? `: ${ideaShort}` : ''}` : null);
  // The Back chip (Layout): where you came from, else Ideas.
  useBackFallback('/ideas', 'Ideas');
  const [loading,       setLoading]       = useState(true);
  const [comments,      setComments]      = useState([]);
  const [saving,        setSaving]        = useState(false);
  const [addingComment, setAddingComment] = useState(false);
  const [editComment,   setEditComment]   = useState({ id: null, text: '' });
  const [newComment,    setNewComment]    = useState('');
  const [pendingStatus, setPendingStatus] = useState(null);
  const [pendingDesc,   setPendingDesc]   = useState('');
  const [people,        setPeople]        = useState([]);   // who @ offers

  const loadComments = useCallback(() => {
    api.get(`/api/ideas/${id}/comments`)
      .then((r) => setComments(r.data))
      .catch(() => {});
  }, [id]);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/api/ideas/${id}`)
      .then((r) => {
        setIdea(r.data);
        setPendingStatus(r.data.Status);
        setPendingDesc(r.data.ShortDescription);
      })
      .catch(() => message.error('Failed to load idea.'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); loadComments(); }, [load, loadComments]);
  useEffect(() => {
    api.get('/api/ideas/people').then((r) => setPeople(r.data || [])).catch(() => setPeople([]));
  }, []);

  const handleSave = async () => {
    if (!pendingDesc.trim()) { message.error('Short description cannot be empty.'); return; }
    if (pendingDesc.trim().length > 100) { message.error('Short description must be 100 characters or fewer.'); return; }

    const body = {};
    if (pendingStatus !== idea.Status)                  body.status            = pendingStatus;
    if (pendingDesc.trim() !== idea.ShortDescription)   body.short_description = pendingDesc.trim();

    const hasFieldChanges = Object.keys(body).length > 0;
    const hasComment      = !!newComment.trim();
    if (!hasFieldChanges && !hasComment) { message.info('No changes to save.'); return; }

    setSaving(true);
    try {
      if (hasFieldChanges) await api.put(`/api/ideas/${id}`, body);
      if (hasComment) {
        const text = newComment.trim();
        await api.post(`/api/ideas/${id}/comments`, {
          comment: text, mention_user_ids: people.filter((p) => text.includes(`@${p.DisplayName}`)).map((p) => p.ID),
        });
        setNewComment('');
        loadComments();
      }
      message.success('Saved.');
      if (hasFieldChanges) load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddComment = async (text, files, mentioned = []) => {
    setAddingComment(true);
    try {
      const res = await api.post(`/api/ideas/${id}/comments`, { comment: text, mention_user_ids: mentioned });
      const commentId = res.data.id;
      for (const f of files) {
        const fd = new FormData();
        fd.append('comment_type', 'idea');
        fd.append('comment_id', String(commentId));
        fd.append('file', f.file, f.name);
        await api.post('/api/attachments', fd);
      }
      setNewComment('');
      loadComments();
    } catch {
      message.error('Failed to add comment.');
    } finally {
      setAddingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await api.delete(`/api/ideas/${id}/comments/${commentId}`);
      loadComments();
    } catch {
      message.error('Failed to delete comment.');
    }
  };

  const handleSaveComment = async () => {
    const text = editComment.text.trim();
    if (!text) return;
    try {
      await api.put(`/api/ideas/${id}/comments/${editComment.id}`, { comment: text });
      setEditComment({ id: null, text: '' });
      loadComments();
    } catch (err) {
      message.error(err?.response?.data?.error || 'Failed to update comment.');
    }
  };

  // Spinner on the first load only: reloads after a save keep the page, so a
  // screenshot attached to an unsent comment is not thrown away.
  if (loading && !idea) return <Spin style={{ display: 'block', padding: 40 }} />;
  if (!idea)   return <Typography.Text type="danger" style={{ padding: 24, display: 'block' }}>Idea not found.</Typography.Text>;

  const isCanceled      = idea.Status === 'canceled';
  const initialComment  = comments.find((c) => c.IsInitial);
  const threadComments  = comments.filter((c) => !c.IsInitial);

  return (
    <div style={{ padding: 24, maxWidth: 860 }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          <BulbOutlined style={{ marginRight: 8 }} />Idea #{id}
        </Typography.Title>
        {isAdmin && (
          <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
            Save
          </Button>
        )}
      </div>

      {/* Header info card */}
      <Card size="small" style={{ marginBottom: 20 }}>
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Submitted By</Typography.Text>
              <div><Typography.Text strong>{idea.UserName}</Typography.Text></div>
            </div>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Team</Typography.Text>
              <div><Typography.Text>{idea.TeamName || '—'}</Typography.Text></div>
            </div>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Submitted</Typography.Text>
              <div><Typography.Text>{formatTS(idea.CreatedAt)}</Typography.Text></div>
            </div>
            {idea.CompletedAt && (
              <div>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>Completed</Typography.Text>
                <div><Typography.Text type="success">{formatTS(idea.CompletedAt)}</Typography.Text></div>
              </div>
            )}
            {idea.CanceledAt && (
              <div>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>Canceled</Typography.Text>
                <div><Typography.Text type="danger">{formatTS(idea.CanceledAt)}</Typography.Text></div>
              </div>
            )}
          </div>

          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Short Description</Typography.Text>
            {isAdmin ? (
              <Input
                value={pendingDesc}
                onChange={(e) => setPendingDesc(e.target.value)}
                maxLength={100}
                showCount
                style={{ marginTop: 4 }}
              />
            ) : (
              <div style={{ marginTop: 2 }}><Typography.Text>{idea.ShortDescription}</Typography.Text></div>
            )}
          </div>

          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Status</Typography.Text>
            {isAdmin ? (
              <div style={{ marginTop: 4 }}>
                <Select
                  value={pendingStatus}
                  onChange={setPendingStatus}
                  options={STATUS_OPTIONS}
                  style={{ width: 190 }}
                />
              </div>
            ) : (
              <div style={{ marginTop: 2 }}>
                <Tag color={STATUS_COLOR[idea.Status]}>{STATUS_LABEL[idea.Status] || idea.Status}</Tag>
              </div>
            )}
          </div>
        </Space>
      </Card>

      {/* Initial comment — styled distinctly */}
      {initialComment && (
        <>
          <Divider orientation="left">
            <Space size={6}><BulbOutlined /><span>Idea Details</span></Space>
          </Divider>
          <Card
            size="small"
            style={{ marginBottom: 20, borderLeft: '3px solid #1677ff', background: '#f0f5ff' }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size={6}>
              <Space size={8}>
                <UserAvatar userId={initialComment.UserID} name={initialComment.UserName} size={24} />
                <Typography.Text strong style={{ fontSize: 13 }}>{initialComment.UserName}</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>{formatTS(initialComment.CreatedAt)}</Typography.Text>
              </Space>
              <Typography.Text style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>
                <LinkifiedText text={initialComment.Comment} />
              </Typography.Text>
              {initialComment.Attachments?.length > 0 && (
                <CommentAttachments attachments={initialComment.Attachments} />
              )}
            </Space>
          </Card>
        </>
      )}

      {/* Thread comments */}
      <Divider orientation="left">
        <Space size={6}><CommentOutlined /><span>Comments</span></Space>
      </Divider>

      {threadComments.length > 0 && (
        <List
          dataSource={threadComments}
          renderItem={(c) => {
            const canEdit   = c.UserID === currentUser?.ID && dayjs().diff(dayjs(c.CreatedAt), 'minute') < 60;
            const canDelete = isAdmin;
            const isEditing = editComment.id === c.ID;
            return (
              <List.Item style={{ alignItems: 'flex-start', padding: '8px 0' }}>
                <List.Item.Meta
                  avatar={<UserAvatar userId={c.UserID} name={c.UserName} size={24} />}
                  title={
                    <Space size={8}>
                      <Typography.Text strong style={{ fontSize: 13 }}>{c.UserName}</Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>{formatTS(c.CreatedAt)}</Typography.Text>
                      {canEdit && !isEditing && (
                        <Button type="text" size="small" icon={<EditOutlined />}
                          onClick={() => setEditComment({ id: c.ID, text: c.Comment })} />
                      )}
                      {canDelete && !isEditing && (
                        <Popconfirm title="Delete this comment?" onConfirm={() => handleDeleteComment(c.ID)}
                          okText="Delete" okButtonProps={{ danger: true }}>
                          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      )}
                    </Space>
                  }
                  description={isEditing ? (
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Input.TextArea rows={3} value={editComment.text} autoFocus
                        onChange={(e) => setEditComment((s) => ({ ...s, text: e.target.value }))} />
                      <Space>
                        <Button type="primary" size="small" onClick={handleSaveComment}>Save</Button>
                        <Button size="small" onClick={() => setEditComment({ id: null, text: '' })}>Cancel</Button>
                      </Space>
                    </Space>
                  ) : (
                    <>
                      <Typography.Text style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}><LinkifiedText text={c.Comment} /></Typography.Text>
                      <CommentAttachments attachments={c.Attachments} />
                    </>
                  )}
                />
              </List.Item>
            );
          }}
        />
      )}

      {!isCanceled ? (
        <div style={{ marginTop: 8 }}>
          <CommentBox
            onSubmit={handleAddComment}
            mentionPeople={people.map((p) => ({ id: p.ID, name: p.DisplayName }))}
            placeholder="Add a comment… (@ to notify someone)"
            loading={addingComment}
            value={newComment}
            onChange={setNewComment}
          />
        </div>
      ) : (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Comments cannot be added to a canceled idea.
        </Typography.Text>
      )}
    </div>
  );
}
